import { differenceInDays } from 'date-fns/differenceInDays';
import i18n from '../../i18n';

export const BOOKING_STATUS = [
  {
    label: i18n.t('bookingStatus.requestForQuote'),
    value: 'requestForQuote',
  },
  {
    label: i18n.t('bookingStatus.quoteSent'),
    value: 'quoteSent',
  },
  {
    label: i18n.t('bookingStatus.quoteDeclined'),
    value: 'quoteDeclined',
  },
  {
    label: i18n.t('bookingStatus.quoteAccepted'),
    value: 'quoteAccepted',
  },
  {
    label: i18n.t('bookingStatus.contractSent'),
    value: 'contractSent',
  },
  {
    label: i18n.t('bookingStatus.abandoned'),
    value: 'abandoned',
  },
  {
    label: i18n.t('bookingStatus.contractSigned'),
    value: 'contractSigned',
  },
  {
    label: i18n.t('bookingStatus.invoiceSent'),
    value: 'invoiceSent',
  },
  {
    label: i18n.t('bookingStatus.paymentValidated'),
    value: 'paymentValidated',
  },
  {
    label: i18n.t('bookingStatus.briefToPrepare'),
    value: 'briefToPrepare',
  },
  {
    label: i18n.t('bookingStatus.briefSent'),
    value: 'briefSent',
  },
  {
    label: i18n.t('bookingStatus.briefConfirmed'),
    value: 'briefConfirmed',
  },
  {
    label: i18n.t('bookingStatus.flightFollow-up'),
    value: 'flightFollow-up',
  },
  {
    label: i18n.t('bookingStatus.completed'),
    value: 'completed',
  },
  {
    label: i18n.t('bookingStatus.canceled'),
    value: 'canceled',
  },
];

const DELETED_USER_IDENTIFIER = 'soft-delete';

/**
 * Check if the new status should trigger a notification
 * @param {string} newStatus The new status
 * @returns {boolean} Returns true if the new status triggers a notification, otherwise false
 */
export const isStatusChangeTriggeringANotification = (newStatus) => {
  const triggerStatus = [
    'quoteSent',
    'contractSent',
    'invoiceSent',
    'briefConfirmed',
    'flightFollow-up',
    'canceled',
  ];
  return triggerStatus.includes(newStatus);
};

/**
 * @param {Booking["passengers"]} passengers - List of passengers
 * @returns {number} Total number of passengers
 */
export const getTotalPassengers = (passengers) => (passengers?.infants || 0)
    + (passengers?.children || 0)
    + (passengers?.adults || 0);

/**
 * Calculate the number of days between departure and arrival dates.
 * @param {string} departureDate - The departure date.
 * @param {string} arrivalDate - The arrival date.
 * @returns {number | null} The number of days between the two dates.
 */
export const daysBetweenDepartureAndArrival = (departureDate, arrivalDate) => {
  const departure = new Date(departureDate);
  const arrival = new Date(arrivalDate);
  if (Number.isNaN(departure.getTime()) || Number.isNaN(arrival.getTime())) {
    return null;
  }
  departure.setHours(0, 0, 0, 0);
  arrival.setHours(0, 0, 0, 0);
  return differenceInDays(arrival, departure);
};

/**
 * Display the full name of a customer.
 * @param {object} customer - Customer object
 * @param {string} [customer.firstname] - Customer first name
 * @param {string} [customer.lastname] - Customer last name
 * @returns {string} Full name of the customer
 */
export const displayCustomerFullName = ({ firstname, lastname }) => {
  const newFirst = firstname?.includes(DELETED_USER_IDENTIFIER) ? '' : firstname;
  const newLast = lastname?.includes(DELETED_USER_IDENTIFIER) ? i18n.t('booking.fields.deletedCustomer') : lastname;
  return `${newFirst || ''} ${newLast || ''}`;
};

/**
 * Redirect to customer filtered list.
 * @param {object} customer - Customer object
 * @param {string} [customer.firstname] - Customer first name
 * @param {string} [customer.lastname] - Customer last name
 * @param {string} [customer.email] - Customer email
 * @returns {string} URL to customer filtered list
 */
export const goToClientDetails = ({ email, firstname, lastname }) => {
  if (firstname?.includes(DELETED_USER_IDENTIFIER) || lastname?.includes(DELETED_USER_IDENTIFIER)) {
    return null;
  }
  return `/customers?search=${encodeURI(email)}&customerfullname=${firstname} ${lastname}`;
};

/**
 * @param {string} bookingId - The booking ID
 */
export const goToEditBooking = (bookingId) => {
  localStorage.setItem('bookingListUrl', window.location.pathname + window.location.search);
  window.location.href = `/bookings/${bookingId}`;
};
